// Healthy Hub theme JS

// HTML-escape for anything interpolated into a template literal that becomes innerHTML.
// Hoisted to module scope on 2026-09-15: an identical local helper already existed inside
// the recently-viewed renderer, but the cart drawer was building markup from live cart
// data with no escaping at all. Product titles on this store contain quotes and
// ampersands, which broke attributes silently. One definition, used by both.
const escHtml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

document.addEventListener('DOMContentLoaded', () => {
  // ---------- Reveal animations ----------
  if (window.HH_SETTINGS && window.HH_SETTINGS.animate) {
    document.documentElement.style.setProperty('--anim-speed', (window.HH_SETTINGS.speed / 1000) + 's');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('[data-animate]').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('[data-animate]').forEach(el => el.style.opacity = 1);
  }

  // ---------- Mobile menu ----------
  const menuBtn     = document.querySelector('[data-menu-toggle]');
  const menuPanel   = document.querySelector('[data-mobile-menu]');
  const menuOverlay = document.querySelector('[data-menu-overlay]');
  const menuClose   = document.querySelector('[data-menu-close]');
  // FIX (2026-09-14): the close timeout was never cancelled, so tapping the overlay and
  // then the hamburger again inside 280ms left hidden=true on both panel and overlay
  // while body overflow stayed locked — an unscrollable page with no menu and nothing
  // left to click. Only a reload recovered. Also: the panels are authored with
  // aria-hidden="true" and it was never flipped on open, so the entire menu was absent
  // from the accessibility tree for screen-reader users.
  let menuCloseTimer = null;
  const openMenu  = () => {
    if (!menuPanel) return;
    clearTimeout(menuCloseTimer);
    menuPanel.hidden = false;
    menuPanel.setAttribute('aria-hidden', 'false');
    if (menuOverlay) { menuOverlay.hidden = false; }
    requestAnimationFrame(() => { menuPanel.classList.add('is-open'); menuOverlay?.classList.add('is-open'); });
    menuBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    menuClose?.focus();
  };
  const closeMenu = () => {
    if (!menuPanel) return;
    clearTimeout(menuCloseTimer);
    menuPanel.classList.remove('is-open');
    menuOverlay?.classList.remove('is-open');
    menuBtn?.setAttribute('aria-expanded', 'false');
    menuPanel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    menuCloseTimer = setTimeout(() => {
      menuPanel.hidden = true;
      if (menuOverlay) menuOverlay.hidden = true;
    }, 280);
  };
  menuBtn?.addEventListener('click', openMenu);
  menuClose?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menuPanel && !menuPanel.hidden) closeMenu();
  });

  // ---------- Mobile search toggle ----------
  const searchBtn   = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-mobile-search]');
  searchBtn?.addEventListener('click', () => {
    if (!searchPanel) return;
    const open = searchPanel.hidden;
    searchPanel.hidden = !open;
    searchBtn.setAttribute('aria-expanded', String(open));
    if (open) { requestAnimationFrame(() => searchPanel.classList.add('is-open')); searchPanel.querySelector('input')?.focus(); }
    else { searchPanel.classList.remove('is-open'); }
  });

  // ---------- Qty steppers (inventory aware) ----------
  const stockLimitMessage = (inv) => `Only ${inv} available in stock — you already reached the limit.`;
  const showStockMsg = (target, msg) => {
    const wrap = target?.closest?.('.pdp__qty-wrap') || target?.closest?.('[data-cart-line]') || target?.closest?.('.product-card') || target?.closest?.('form') || target?.parentElement;
    let el = wrap?.querySelector('[data-stock-error]');
    if (!el && wrap && msg) {
      el = document.createElement('small');
      el.className = 'stock-error';
      el.setAttribute('data-stock-error', '');
      el.innerHTML = '<span data-stock-error-text></span><button type="button" class="stock-error__close" data-stock-error-close aria-label="Dismiss stock warning">×</button>';
      wrap.appendChild(el);
    }
    if (!el) return;
    const textEl = el.querySelector('[data-stock-error-text]') || el;
    if (msg) {
      textEl.textContent = msg;
      el.hidden = false;
      if (el._hhTimer) clearTimeout(el._hhTimer);
      el._hhTimer = setTimeout(() => { el.hidden = true; textEl.textContent = ''; }, 10000);
    } else {
      if (el._hhTimer) clearTimeout(el._hhTimer);
      textEl.textContent = '';
      el.hidden = true;
    }
  };
  document.addEventListener('click', (e) => {
    const close = e.target.closest('[data-stock-error-close]');
    if (!close) return;
    e.preventDefault();
    e.stopPropagation();
    const el = close.closest('[data-stock-error]');
    if (el?._hhTimer) clearTimeout(el._hhTimer);
    const textEl = el?.querySelector('[data-stock-error-text]') || el;
    if (textEl) textEl.textContent = '';
    if (el) el.hidden = true;
  });
  const clampToInventory = (group, val) => {
    const inv = parseInt(group.dataset.inventory || '-1', 10);
    if (inv >= 0 && val > inv) {
      showStockMsg(group, stockLimitMessage(inv));
      return inv;
    }
    // do not auto-clear; let the timer expire so the user sees the message
    return val;
  };
  document.querySelectorAll('[data-qty]').forEach(group => {
    const input = group.querySelector('input');
    if (!input) return;
    group.querySelector('[data-qty-down]')?.addEventListener('click', () => {
      input.value = Math.max(parseInt(input.min || '1', 10), parseInt(input.value || '1', 10) - 1);
    });
    group.querySelector('[data-qty-up]')?.addEventListener('click', () => {
      const next = parseInt(input.value || '1', 10) + 1;
      input.value = clampToInventory(group, next);
    });
    input.addEventListener('change', () => {
      let v = parseInt(input.value || '1', 10);
      if (isNaN(v) || v < parseInt(input.min || '1', 10)) v = parseInt(input.min || '1', 10);
      input.value = clampToInventory(group, v);
    });
  });
  document.querySelector('[data-cart-page-form]')?.addEventListener('submit', (e) => {
    const form = e.currentTarget;
    let blocked = false;
    form.querySelectorAll('[data-cart-line]').forEach(line => {
      const input = line.querySelector('input[name="updates[]"]');
      const inv = parseInt(line.dataset.inventory || '-1', 10);
      const qty = parseInt(input?.value || '0', 10);
      if (inv >= 0 && qty > inv) {
        input.value = inv;
        showStockMsg(line, stockLimitMessage(inv));
        blocked = true;
      }
    });
    if (blocked) e.preventDefault();
  });

  // ---------- PDP thumbs / variants ----------
  document.querySelectorAll('[data-thumb]').forEach(btn => {
    btn.addEventListener('click', () => {
      const main = document.querySelector('[data-main-image]');
      if (main) main.src = btn.dataset.thumb;
      document.querySelectorAll('[data-thumb]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
    });
  });
  // ---------- PDP variant selection ----------
  // CRITICAL FIX (2026-09-14): this used to toggle an `is-active` class and nothing
  // else. input[name="id"] was never updated, so whatever the shopper picked, the
  // FIRST AVAILABLE variant was added to the cart at that variant's price. Price,
  // stock and the inventory cap were equally frozen. Now the selected option values
  // are resolved against the real variant table emitted by main-product.liquid.
  (function initVariantPicker() {
    const groups = Array.from(document.querySelectorAll('[data-variant-group]'));
    if (!groups.length) return;

    const dataEl = document.querySelector('[data-variant-data]');
    let variants = [];
    try { variants = JSON.parse(dataEl?.textContent || '[]'); } catch (_) { variants = []; }
    if (!variants.length) return;

    const idInput  = document.querySelector('[data-variant-id-input]');
    const priceEl  = document.querySelector('[data-pdp-price]');
    const compEl   = document.querySelector('[data-pdp-compare]');
    const stockEl  = document.querySelector('[data-pdp-stock]');
    const qtyEl    = document.querySelector('[data-qty]');
    const qtyInput = qtyEl?.querySelector('input[name="quantity"]');
    const addBtn   = document.querySelector('[data-pdp-add]');
    const buyBtn   = document.querySelector('[data-pdp-buy]');
    const mainImg  = document.querySelector('[data-main-image]');

    const selectedValues = () => groups
      .slice()
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map(g => g.querySelector('.pdp__variant.is-active')?.dataset.optionValue ?? null);

    const apply = () => {
      const chosen = selectedValues();
      if (chosen.some(v => v === null)) return;
      const match = variants.find(v =>
        Array.isArray(v.options) &&
        v.options.length === chosen.length &&
        v.options.every((o, i) => String(o) === String(chosen[i]))
      );
      if (!match) return;

      if (idInput) idInput.value = match.id;
      if (priceEl) priceEl.textContent = match.price;
      if (compEl) {
        if (match.compare_at_price) { compEl.textContent = match.compare_at_price; compEl.hidden = false; }
        else { compEl.textContent = ''; compEl.hidden = true; }
      }
      if (stockEl) stockEl.textContent = '● ' + (match.available ? 'In stock' : 'Sold out');

      // Keep the stock guard pointed at the variant the shopper actually selected.
      if (qtyEl) {
        qtyEl.dataset.variantId = match.id;
        qtyEl.dataset.inventory = match.inventory;
      }
      if (qtyInput) {
        if (match.inventory > 0) qtyInput.max = match.inventory; else qtyInput.removeAttribute('max');
        if (Number(qtyInput.value) > match.inventory && match.inventory > 0) qtyInput.value = match.inventory;
      }

      [addBtn, buyBtn].forEach(btn => {
        if (!btn) return;
        btn.disabled = !match.available;
        btn.setAttribute('aria-disabled', String(!match.available));
      });
      if (addBtn) addBtn.textContent = match.available ? 'Add to cart' : 'Sold out';

      if (match.image && mainImg) mainImg.src = match.image;
    };

    groups.forEach(group => {
      group.querySelectorAll('.pdp__variant').forEach(v => {
        v.addEventListener('click', () => {
          group.querySelectorAll('.pdp__variant').forEach(x => {
            x.classList.remove('is-active');
            x.setAttribute('aria-pressed', 'false');
          });
          v.classList.add('is-active');
          v.setAttribute('aria-pressed', 'true');
          apply();
        });
      });
    });

    apply();
  })();

  // Wishlist removed

  // ============================================================
  // Cart popup / drawer
  // ============================================================
  const drawer       = document.querySelector('[data-cart-drawer]');
  const drawerBody   = document.querySelector('[data-cart-drawer-body]');
  const drawerFooter = document.querySelector('[data-cart-drawer-footer]');
  const drawerSubtot = document.querySelector('[data-cart-subtotal]');
  const drawerOverlay= document.querySelector('[data-cart-overlay]');
  const cartToggle   = document.querySelector('[data-cart-toggle]');
  const cartClose    = document.querySelector('[data-cart-close]');
  const cartStyle    = drawer?.dataset.cartStyle || 'popup'; // popup | drawer
  let cartActionBusy = false;
  let cartUpdateBusy = false;
  let cartHasLoaded = false;

  // Move overlay to <body> so it isn't trapped inside the header's stacking context.
  // Header has z-index 1002 and the overlay needs to sit at z-index 1000 covering the
  // viewport BELOW the header — that's only possible when it's a child of <body>.
  if (drawerOverlay && drawerOverlay.parentElement !== document.body) {
    document.body.appendChild(drawerOverlay);
  }

  // const money = (cents) => {
  //   try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format((cents || 0) / 100); }
  //   catch (_) { return '$' + ((cents || 0) / 100).toFixed(2); }
  // };

  const money = (cents) => {
  try {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol'
    })
      .format((cents || 0) / 100)
      .replace(/^[A-Z]{2,3}(?=\s?[$€£¥₹])\s?/, '');

  } catch (_) {
    return '$' + ((cents || 0) / 100).toFixed(2);
  }
};

  const getCartLoader = () => {
    if (!drawer) return null;
    let loader = drawer.querySelector('[data-cart-loading]');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'cart-drawer__loading';
      loader.setAttribute('data-cart-loading', '');
      loader.innerHTML = '<span class="cart-spinner" aria-label="Loading cart"></span>';
      drawer.appendChild(loader);
    }
    return loader;
  };
  const setCartLoading = (loading) => {
    if (!drawer) return;
    drawer.classList.toggle('is-loading', Boolean(loading));
    getCartLoader()?.setAttribute('aria-hidden', loading ? 'false' : 'true');
  };
  const setLineBusy = (line, busy) => {
    if (!line) return;
    line.classList.toggle('is-loading', Boolean(busy));
    line.querySelectorAll('button,input,a[data-cart-line-remove]').forEach(el => {
      if ('disabled' in el) el.disabled = Boolean(busy);
      if (el.matches('a')) el.setAttribute('aria-disabled', busy ? 'true' : 'false');
    });
  };

  const updateCountBadges = (n) => {
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = n; });
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    return res.json();
  };
  const getCartQuantity = (cart, variantId) => {
    const id = String(variantId || '');
    const line = cart?.items?.find(item => String(item.variant_id) === id);
    return line ? parseInt(line.quantity || '0', 10) : 0;
  };
  const variantInventoryCache = {};
  const getInventoryLimit = async (variantId, fallback = -1, productHandle = '') => {
    const fb = parseInt(fallback, 10);
    if (fb >= 0) return fb;
    const id = String(variantId || '');
    if (!id) return -1;
    if (Object.prototype.hasOwnProperty.call(variantInventoryCache, id)) return variantInventoryCache[id];
    try {
      if (productHandle) {
        const productRes = await fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}.js`, { headers: { 'Accept': 'application/json' } });
        const product = await productRes.json();
        const variant = product?.variants?.find(v => String(v.id) === id);
        const managed = variant?.inventory_management || variant?.inventory_policy === 'deny';
        let inv = managed && Number.isFinite(parseInt(variant?.inventory_quantity, 10)) ? parseInt(variant.inventory_quantity, 10) : -1;
        if (inv < 0) {
          const htmlRes = await fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}?variant=${id}`, { headers: { 'Accept': 'text/html' } });
          const html = await htmlRes.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector(`[data-variant-id="${id}"][data-inventory]`);
          inv = Number.isFinite(parseInt(source?.dataset?.inventory, 10)) ? parseInt(source.dataset.inventory, 10) : -1;
        }
        variantInventoryCache[id] = inv;
        return inv;
      }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}variants/${id}.js`, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      const managed = data.inventory_management || data.inventory_policy === 'deny';
      const inv = managed && Number.isFinite(parseInt(data.inventory_quantity, 10)) ? parseInt(data.inventory_quantity, 10) : -1;
      variantInventoryCache[id] = inv;
      return inv;
    } catch (_) {
      variantInventoryCache[id] = -1;
      return -1;
    }
  };
  const canAddQuantity = async ({ variantId, quantity, inventory, target }) => {
    const productHandle = target?.dataset?.productHandle || target?.closest?.('[data-product-handle], [data-pdp]')?.dataset?.productHandle || '';
    const limit = await getInventoryLimit(variantId, inventory, productHandle);
    if (limit < 0) return true;
    const cart = await fetchCart();
    const inCart = getCartQuantity(cart, variantId);
    const requested = parseInt(quantity || '1', 10) || 1;
    if (inCart + requested > limit) {
      showStockMsg(target, stockLimitMessage(limit));
      return false;
    }
    return true;
  };

  const renderCart = async ({ showLoader = false } = {}) => {
    if (showLoader) setCartLoading(true);
    try {
      const cart = await fetchCart();
      updateCountBadges(cart.item_count);
      if (!drawer) return cart;
      if (!cart.items || cart.items.length === 0) {
        drawerBody.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
        drawerFooter.hidden = true;
        cartHasLoaded = true;
        return cart;
      }
      const inventoryByVariant = {};
      await Promise.all(cart.items.map(async (it) => {
        inventoryByVariant[it.variant_id] = await getInventoryLimit(it.variant_id, -1, it.handle || '');
      }));
      drawerBody.innerHTML = cart.items.map(it => {
        const inv = inventoryByVariant[it.variant_id] ?? -1;
        // FIX (2026-09-15): every one of these values used to be interpolated raw into
        // markup and attribute positions. A product title containing a double quote —
        // e.g. Vitamin D3 1000 IU "Extra Strength" — closed the alt="" attribute early
        // and corrupted the rest of the <img> tag and the remove button's accessible
        // name; & and < mangled the visible title. This is the same defect the
        // recently-viewed rewrite fixed with esc() on 2026-09-14; renderCart was missed.
        return `
        <div class="cart-drawer__item" data-cart-line data-line-key="${escHtml(it.key)}" data-variant-id="${escHtml(it.variant_id)}" data-inventory="${inv}">
          <img src="${escHtml(it.image || '')}" alt="${escHtml(it.product_title)}">
          <div class="cart-drawer__item-main">
            <a href="${escHtml(it.url)}" class="cart-drawer__title">${escHtml(it.product_title)}</a>
            ${it.variant_title ? `<p class="cart-drawer__variant">${escHtml(it.variant_title)}</p>` : ''}
            <div class="cart-drawer__row">
              <div class="cart-drawer__qty" data-qty data-variant-id="${escHtml(it.variant_id)}" data-inventory="${inv}">
                <button type="button" data-cart-qty="down" aria-label="Decrease">−</button>
                <input type="number" value="${it.quantity}" min="0" ${inv > 0 ? `max="${inv}"` : ''} data-cart-qty-input>
                <button type="button" data-cart-qty="up" aria-label="Increase">+</button>
              </div>
              <strong>${money(it.final_line_price)}</strong>
            </div>
            <small class="stock-error" data-stock-error hidden><span data-stock-error-text></span><button type="button" class="stock-error__close" data-stock-error-close aria-label="Dismiss stock warning">×</button></small>
            <div class="cart-drawer__item-actions">
              <button type="button" class="cart-line-remove" data-cart-line-remove data-line-key="${escHtml(it.key)}" data-variant-id="${escHtml(it.variant_id)}" aria-label="Remove ${escHtml(it.product_title)}">✕ Remove</button>
            </div>
          </div>
        </div>`;
      }).join('');
      drawerSubtot.textContent = money(cart.total_price);
      drawerFooter.hidden = false;
      cartHasLoaded = true;
      return cart;
    } catch (err) { console.error('Cart render error', err); }
    finally { if (showLoader) setCartLoading(false); }
  };

  // Drawer-line qty change via /cart/change.js
  drawerBody?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;
    if (cartUpdateBusy) return;
    const line = btn.closest('[data-cart-line]');
    const input = line.querySelector('[data-cart-qty-input]');
    const dir = btn.dataset.cartQty;
    let next = parseInt(input.value || '0', 10) + (dir === 'up' ? 1 : -1);
    if (next < 0) next = 0;
    const inv = parseInt(line.dataset.inventory || '-1', 10);
    if (inv >= 0 && next > inv) {
      input.value = inv;
      showStockMsg(line, stockLimitMessage(inv));
      return;
    }
    try {
      cartUpdateBusy = true;
      setLineBusy(line, true);
      setCartLoading(true);
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line.dataset.lineKey || line.dataset.variantId, quantity: next })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showStockMsg(line, data.description || 'Could not update this item.');
        return;
      }
      await renderCart();
    } catch (err) { console.error(err); }
    finally { cartUpdateBusy = false; setLineBusy(line, false); setCartLoading(false); }
  });
  drawerBody?.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    if (cartUpdateBusy) return;
    const line = input.closest('[data-cart-line]');
    let next = parseInt(input.value || '0', 10);
    if (isNaN(next) || next < 0) next = 0;
    const inv = parseInt(line.dataset.inventory || '-1', 10);
    if (inv >= 0 && next > inv) {
      input.value = inv;
      showStockMsg(line, stockLimitMessage(inv));
      return;
    }
    input.value = next;
    try {
      cartUpdateBusy = true;
      setLineBusy(line, true);
      setCartLoading(true);
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line.dataset.lineKey || line.dataset.variantId, quantity: next })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showStockMsg(line, data.description || 'Could not update this item.');
        return;
      }
      await renderCart();
    } catch (err) { console.error(err); }
    finally { cartUpdateBusy = false; setLineBusy(line, false); setCartLoading(false); }
  });

  // Remove line (drawer + cart page)
  const updateCartPageTotals = (cart) => {
    document.querySelector('[data-cart-page-title]') && (document.querySelector('[data-cart-page-title]').textContent = `${cart.item_count} items in your cart`);
    document.querySelector('[data-cart-page-subtotal]') && (document.querySelector('[data-cart-page-subtotal]').textContent = money(cart.total_price));
  };
  const removeLine = async (lineId, lineEl) => {
    if (cartUpdateBusy || !lineId) return null;
    try {
      cartUpdateBusy = true;
      setLineBusy(lineEl, true);
      setCartLoading(true);
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineId, quantity: 0 })
      });
      const cart = await res.json().catch(() => null);
      if (!res.ok) throw new Error(cart?.description || 'Could not remove this item.');
      updateCountBadges(cart.item_count);
      updateCartPageTotals(cart);
      await renderCart();
      return cart;
    } catch (err) { console.error(err); }
    finally { cartUpdateBusy = false; setLineBusy(lineEl, false); setCartLoading(false); }
    return null;
  };
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-line-remove]');
    if (!btn) return;
    e.preventDefault();
    const line = btn.closest('[data-cart-line]');
    const lineId = btn.dataset.lineKey || line?.dataset?.lineKey || btn.dataset.variantId;
    if (!lineId) return;
    const cart = await removeLine(lineId, line);
    if (!cart) return;
    // If on cart page, also remove the DOM node so the page reflects immediately
    if (line && document.querySelector('[data-cart-page-form]')) {
      line.remove();
      if (!document.querySelector('[data-cart-page-form] [data-cart-line]')) {
        const container = document.querySelector('[data-cart-page-container]');
        const form = document.querySelector('[data-cart-page-form]');
        if (form) form.remove();
        container?.insertAdjacentHTML('beforeend', '<p>Your cart is empty.</p><a href="/collections/all" class="btn btn--primary">Continue shopping</a>');
      }
    }
  });

  const lockScroll = () => {
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.dataset.hhScrollY = String(window.scrollY);
    // document.body.style.paddingRight = sbw > 0 ? sbw + 'px' : '';
    document.body.classList.add('hh-scroll-locked');
  };
  const unlockScroll = () => {
    document.body.classList.remove('hh-scroll-locked');
    document.body.style.paddingRight = '';
    delete document.body.dataset.hhScrollY;
  };
  // FIX (2026-09-14): same uncancelled-timeout race as the mobile menu, plus the
  // drawer was authored aria-hidden="true" and never flipped, hiding the whole cart
  // (qty steppers, Checkout) from screen readers while it was visually open.
  // Declared before openDrawer so both closures capture the same binding.
  let drawerCloseTimer = null;
  const openDrawer = ({ refresh = true } = {}) => {
    if (!drawer) return;
    clearTimeout(drawerCloseTimer);
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    if (drawerOverlay) { drawerOverlay.hidden = false; }
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawerOverlay?.classList.add('is-open');
    });
    lockScroll();
    cartToggle?.setAttribute('aria-expanded', 'true');
    if (refresh) renderCart({ showLoader: cartHasLoaded });
  };
  const closeDrawer = () => {
    if (!drawer) return;
    clearTimeout(drawerCloseTimer);
    drawer.classList.remove('is-open');
    drawerOverlay?.classList.remove('is-open');
    cartToggle?.setAttribute('aria-expanded', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    drawerCloseTimer = setTimeout(() => {
      drawer.hidden = true;
      if (drawerOverlay) drawerOverlay.hidden = true;
    }, 280);
    document.removeEventListener('click', outsideDrawerClick);
  };
  const outsideDrawerClick = (e) => { if (!drawer.contains(e.target) && cartToggle && !cartToggle.contains(e.target)) closeDrawer(); };
  // FIX (2026-09-15): this tested `drawer.hidden`, which closeDrawer only sets after a
  // 280ms transition timeout. A second click inside that window therefore saw
  // hidden === false and called closeDrawer() AGAIN — re-arming the timer instead of
  // reopening — so double-tapping the cart icon left the drawer shut. Same class of bug
  // as the mobile-menu timer fix above; test the class that is applied synchronously.
  cartToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    drawer.classList.contains('is-open') ? closeDrawer() : openDrawer();
  });
  cartClose?.addEventListener('click', closeDrawer);
  drawerOverlay?.addEventListener('click', closeDrawer);
  // Escape closes the cart the same way it closes the menu.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && !drawer.hidden) closeDrawer();
  });
  window.HH_openCartDrawer = openDrawer;
  // Initial badge refresh only (do NOT auto-show drawer)
  if (drawer) {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json()).then(c => updateCountBadges(c.item_count)).catch(()=>{});
  }

  // ============================================================
  // AJAX add to cart -> open drawer/popup (PDP form + quick add)
  // ============================================================
  const showAddError = (target, msg) => {
    showStockMsg(target, msg);
  };
  const ajaxAdd = async (formData) => {
    const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || 'add failed');
      err.data = data;
      throw err;
    }
    return data;
  };

  // PDP form
  document.querySelectorAll('form[action$="/cart/add"], #product-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      const submitter = e.submitter;
      const isCheckout = submitter && submitter.name === 'checkout';
      e.preventDefault();
      if (cartActionBusy) return;
      const btn = form.querySelector('button[type="submit"]:not([name="checkout"])');
      const orig = btn ? btn.textContent : '';
      const submitButtons = form.querySelectorAll('button[type="submit"]');
      cartActionBusy = true;
      submitButtons.forEach(b => { b.disabled = true; });
      if (btn) { btn.classList.add('is-adding'); btn.innerHTML = '<span class="cart-spinner" aria-hidden="true"></span>Adding…'; }
      if (drawer && !isCheckout) { openDrawer({ refresh: false }); setCartLoading(true); }
      const qtyGroup = form.querySelector('[data-qty]') || form;
      const fd = new FormData(form);
      const variantId = fd.get('id');
      const quantity = fd.get('quantity') || '1';
      const inventory = qtyGroup?.dataset?.inventory || -1;
      try {
        const ok = await canAddQuantity({ variantId, quantity, inventory, target: qtyGroup });
        // Same skeleton dead-end as the quick-add path: if we bail out with the drawer
        // already open, render the real cart first so it never sticks on the loader.
        if (!ok) {
          if (drawer && !isCheckout) { await renderCart(); }
          return;
        }
        await ajaxAdd(fd);
        if (isCheckout) window.location = '/checkout';
        else if (drawer) { await renderCart(); openDrawer({ refresh: false }); }
        else window.location = '/cart';
      }
      catch (err) {
        const msg = err.data?.description || 'Could not add to cart';
        showAddError(qtyGroup, msg);
        if (drawer && !isCheckout) { try { await renderCart(); } catch (_) {} }
      }
      finally {
        cartActionBusy = false;
        setCartLoading(false);
        submitButtons.forEach(b => { b.disabled = false; });
        if (btn) { btn.classList.remove('is-adding'); btn.textContent = orig; }
      }
    });
  });

  // Quick add buttons
  document.addEventListener('click', async (e) => {
    const qa = e.target.closest('[data-quick-add]');
    if (!qa) return;
    e.preventDefault(); e.stopPropagation();
    if (cartActionBusy) return;
    const id = qa.dataset.variantId;
    if (!id) return;
    const orig = qa.textContent;
    cartActionBusy = true;
    qa.disabled = true; qa.classList.add('is-adding'); qa.innerHTML = '<span class="cart-spinner" aria-hidden="true"></span>Adding…';
    if (drawer) { openDrawer({ refresh: false }); setCartLoading(true); }
    try {
      const ok = await canAddQuantity({ variantId: id, quantity: 1, inventory: qa.dataset.inventory || -1, target: qa });
      // FIX (2026-09-14): this used to `return` with the drawer already open and still
      // showing its server-rendered loading skeleton — renderCart() is the only thing
      // that ever replaces that markup. A shopper who quick-added an item already at
      // its stock limit got three shimmering grey bars and no Checkout button,
      // permanently, with the stock reason written onto the card *behind* the overlay.
      // Render the real cart (or close, if the cart is empty) before bailing out.
      if (!ok) {
        if (drawer) { await renderCart(); }
        return;
      }
      const fd = new FormData(); fd.append('id', id); fd.append('quantity', '1');
      await ajaxAdd(fd);
      if (drawer) { await renderCart(); openDrawer({ refresh: false }); } else window.location = '/cart';
    } catch (err) {
      console.error(err);
      showAddError(qa, err.data?.description || 'Out of stock');
      // Same dead-end as above: never leave the drawer stuck on the skeleton.
      if (drawer) { try { await renderCart(); } catch (_) {} }
    }
    finally { cartActionBusy = false; setCartLoading(false); qa.disabled = false; qa.classList.remove('is-adding'); qa.textContent = orig; }
  });

  // ============================================================
  // Mega menu hover (desktop)
  // ============================================================
  // A11Y FIX (2026-09-15): header.liquid authors aria-expanded on .header__nav-link, the
  // actual control. Nothing ever updated it — the only code touching aria-expanded was
  // initMegaMenu() further down, which wrote it to .header__nav-arrow, a decorative span
  // carrying aria-hidden="true" where the attribute is ignored outright. So the trigger
  // reported aria-expanded="false" for the whole life of the page while the menu opened
  // and closed under it. This handler runs at every width; initMegaMenu bails outside
  // 1024-1440px, so the state has to be maintained here.
  document.querySelectorAll('.header__nav-item.has-mega').forEach(item => {
    let t;
    const trigger = item.querySelector('.header__nav-link');
    const open  = () => { clearTimeout(t); item.classList.add('is-open'); trigger?.setAttribute('aria-expanded', 'true'); };
    const close = () => { t = setTimeout(() => { item.classList.remove('is-open'); trigger?.setAttribute('aria-expanded', 'false'); }, 120); };
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', close);
  });

  // ============================================================
  // Testimonials slider (when >4 reviews)
  // ============================================================
  document.querySelectorAll('[data-testimonial-slider="true"]').forEach(track => {
    const parent = track.parentElement;
    const prev = parent.querySelector('[data-slider-prev]');
    const next = parent.querySelector('[data-slider-next]');
    const step = () => Math.max(280, track.firstElementChild?.offsetWidth + 24 || 320);
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left:  step(), behavior: 'smooth' }));
  });

  // ============================================================
  // Sticky header + announcement (scroll-driven)
  // ============================================================
  const ann    = document.querySelector('[data-announcement]');
  const header = document.querySelector('.header');
  const annSticky    = ann    && ann.getAttribute('data-sticky') === 'true';
  const headerSticky = header && header.getAttribute('data-sticky') === 'true';
  if (annSticky || headerSticky) {
    const annH = () => (annSticky && ann ? ann.offsetHeight : 0);
    const hdrH = () => (header ? header.offsetHeight : 0);
    const setVars = () => {
      document.documentElement.style.setProperty('--ann-h', annH() + 'px');
      document.documentElement.style.setProperty('--header-fixed-pad', (annH() + hdrH()) + 'px');
    };
    setVars();
    let lastStuck = false;
    const onScroll = () => {
      const stuck = window.scrollY > 4;
      if (stuck !== lastStuck) {
        lastStuck = stuck;
        if (annSticky)    ann.classList.toggle('is-stuck', stuck);
        if (headerSticky) header.classList.toggle('is-stuck', stuck);
        document.body.classList.toggle('has-fixed-header', stuck);
        setVars();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setVars);
  }

  // ============================================================
  // 3-Column Dynamic Mega Menu (collections → products → preview)
  // ============================================================
  document.querySelectorAll('[data-mega-triple]').forEach(mega => {
    const collections = mega.querySelectorAll('.mm-collection');
    const productLists = mega.querySelectorAll('.mm-products');
    const products = mega.querySelectorAll('.mm-product[data-product-id]');
    const previews = mega.querySelectorAll('.mm-preview');

    const activateProduct = (id) => {
      products.forEach(p => {
        const on = p.dataset.productId === id;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      previews.forEach(pv => pv.classList.toggle('is-active', pv.dataset.previewFor === id));
    };

    const activateCollection = (handle) => {
      collections.forEach(c => {
        const on = c.dataset.collection === handle;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-selected', on ? 'true' : 'false');
        c.setAttribute('tabindex', on ? '0' : '-1');
      });
      productLists.forEach(pl => pl.classList.toggle('is-active', pl.dataset.productsFor === handle));
      const activeList = mega.querySelector('.mm-products.is-active');
      if (activeList) {
        const firstProduct = activeList.querySelector('.mm-product[data-product-id]');
        if (firstProduct) {
          activateProduct(firstProduct.dataset.productId);
        } else {
          previews.forEach(p => p.classList.toggle('is-active', p.dataset.previewFor === handle + '-empty'));
        }
      }
    };

    collections.forEach(c => {
      const handler = () => activateCollection(c.dataset.collection);
      c.addEventListener('mouseenter', handler);
      c.addEventListener('focus', handler);
    });

    products.forEach(p => {
      const handler = () => activateProduct(p.dataset.productId);
      p.addEventListener('mouseenter', handler);
      p.addEventListener('focus', handler);
    });

    // Keyboard navigation
    mega.addEventListener('keydown', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const inCol1 = target.classList.contains('mm-collection');
      const inCol2 = target.classList.contains('mm-product') && target.hasAttribute('data-product-id');
      if (!inCol1 && !inCol2) return;

      const siblings = inCol1
        ? Array.from(collections)
        : Array.from(mega.querySelectorAll('.mm-products.is-active .mm-product[data-product-id]'));
      const idx = siblings.indexOf(target);

      let next = null;
      if (e.key === 'ArrowDown' && idx > -1) next = siblings[Math.min(idx + 1, siblings.length - 1)];
      else if (e.key === 'ArrowUp' && idx > -1) next = siblings[Math.max(idx - 1, 0)];
      else if (e.key === 'ArrowRight' && inCol1) {
        next = mega.querySelector('.mm-products.is-active .mm-product[data-product-id]');
      } else if (e.key === 'ArrowLeft' && inCol2) {
        next = mega.querySelector('.mm-collection.is-active');
      } else if (e.key === 'Escape') {
        const item = mega.closest('.header__nav-item');
        if (item) item.classList.remove('is-open');
        const trigger = item && item.querySelector('.header__nav-link');
        if (trigger) trigger.focus();
        return;
      }

      if (next) {
        e.preventDefault();
        next.focus();
        if (inCol1) collections.forEach(c => c.setAttribute('tabindex', c === next ? '0' : '-1'));
      }
    });
  });
});

// ============================================================
// v7: Recently viewed
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
  // Facet inputs: submit form on change (single delegated handler)
  // REMOVED (2026-09-14) — this was an orphaned earlier implementation and it fought
  // the live one. sections/main-collection.liquid already binds `change` directly on
  // every [data-facet-input] and filters client-side via history.pushState. This
  // delegated handler fired straight afterwards and called facetForm.submit(), which
  // bypasses both onsubmit="return false;" and the form's submit listener, forcing a
  // real navigation: the drawer was destroyed and reopened closed, scroll jumped to
  // the top, and sort_by (which lives in a separate #SortForm) was dropped. Selecting
  // three brands meant three full page loads. Deleting it restores the intended
  // client-side filtering.

  // Recently viewed: track on PDP, render on dedicated section
  const RV_KEY = 'hh_recently_viewed';
  const getRV = () => { try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (_) { return []; } };
  const setRV = (a) => { try { localStorage.setItem(RV_KEY, JSON.stringify(a.slice(0, 16))); } catch(_){} };

  const pdp = document.querySelector('[data-pdp]');
  if (pdp && pdp.dataset.productHandle) {
    const item = {
      handle: pdp.dataset.productHandle,
      id: pdp.dataset.productId,
      title: pdp.dataset.productTitle,
      url: pdp.dataset.productUrl,
      image: pdp.dataset.productImage,
      price: pdp.dataset.productPrice,
      vendor: pdp.dataset.productVendor,
      // Stored so the Recently viewed card can render a working quick-add button
      // instead of a dead one. Only set for single-variant products — a multi-variant
      // product must be chosen on the PDP, so its card links through instead.
      variantId: pdp.dataset.productVariantId || null,
      inventory: pdp.dataset.productInventory || null,
      ts: Date.now()
    };
    let list = getRV().filter(p => p.handle !== item.handle);
    list.unshift(item);
    setRV(list);
  }

  const rvSection = document.querySelector('[data-recently-viewed-section]');
  const rvGrid    = document.querySelector('[data-recently-viewed-grid]');
  if (rvSection && rvGrid) {
    const exclude = rvSection.dataset.excludeHandle || '';
    const items = getRV().filter(p => p.handle && p.handle !== exclude).slice(0, 6);
    if (items.length === 0) {
      rvSection.hidden = true;
    } else {
      rvSection.hidden = false;
      // FIX (2026-09-14): three defects in this template.
      // 1. Values came from localStorage straight into HTML and attribute positions
      //    with no escaping (only p.title got a partial quote replace), so a product
      //    title containing " or < corrupted the markup.
      // 2. The Add To Cart button carried no data-quick-add / data-variant-id, so the
      //    delegated handler never matched, nothing called preventDefault(), and the
      //    click fell through to the wrapping <a> — it navigated instead of adding.
      // 3. A <button> nested inside an <a> is invalid HTML. The card is now a
      //    positioned anchor with the action button as a sibling, not a descendant.
      // Was defined here; now shared with the cart drawer from module scope (2026-09-15).
      const esc = escHtml;
      const truncate = (s, n) => (String(s || '').length > n ? String(s).substring(0, n) + '…' : String(s || ''));

      rvGrid.innerHTML = items.map(p => `
        <div class="product-card">
          <a href="${esc(p.url)}" class="product-card__link">
            <div class="product-card__media">
              ${p.image ? `<img src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy" width="600" height="600">` : ''}
            </div>
            <div class="product-card__body">
              <div>
                ${p.vendor ? `<span class="product-card__brand">${esc(p.vendor)}</span><br>` : ''}
                <span class="product-card__title">${esc(truncate(p.title, 40))}</span>
              </div>
            </div>
          </a>
          <div class="product-card-bottom">
            <div class="product-card__price"><span class="amount">${esc(p.price)}</span></div>
            ${
              p.vendor !== "Zonnic"
                ? (p.variantId
                    ? `<button type="button" class="product-card__quick" data-quick-add data-variant-id="${esc(p.variantId)}"${p.inventory != null ? ` data-inventory="${esc(p.inventory)}"` : ''}>Add To Cart</button>`
                    : `<a href="${esc(p.url)}" class="product-card__quick">View Product</a>`)
                : `<div class="zonic-message"><p>This product requires age verification before purchase.</p></div>`
            }
          </div>
        </div>
      `).join('');
    }
  }
});

// ============================================================
// v9 — Mobile/tablet announcement slider (swipe + autoplay)
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-announcement-slider]').forEach(slider => {
    const track  = slider.querySelector('[data-announcement-track]');
    const slides = track ? Array.from(track.querySelectorAll('.announcement__slide')) : [];
    if (!track || slides.length < 2) return;

    // Hide dot indicators if present (user requested no dots)
    slider.querySelectorAll('[data-announcement-dots], .announcement__dots').forEach(d => d.remove());

    const root = slider.closest('[data-announcement]');
    const delay = Math.max(2, parseInt(root?.dataset.autoplay || '3', 10)) * 1000;
    const total = slides.length;
    let perView = window.matchMedia('(max-width: 767px)').matches ? 1 : 2;
    let index = 0; let timer = null; let userPaused = false;

    // Clone the first `perView` slides and append, so that stepping past the end
    // continues smoothly left→right. After the cloned region we snap back to 0
    // without a visible transition.
    const ensureClones = () => {
      track.querySelectorAll('[data-announcement-clone]').forEach(n => n.remove());
      for (let i = 0; i < perView; i++) {
        const c = slides[i].cloneNode(true);
        c.setAttribute('data-announcement-clone', '');
        c.setAttribute('aria-hidden', 'true');
        track.appendChild(c);
      }
    };

    const stepPercent = () => 100 / perView; // one slide width as a % of viewport

    const apply = (withTransition) => {
      track.style.transition = withTransition ? 'transform .5s cubic-bezier(.5,.1,.25,1)' : 'none';
      track.style.transform = `translateX(-${index * stepPercent()}%)`;
    };

    const next = () => {
      index += 1;
      apply(true);
      if (index >= total) {
        // After the slide-in animation finishes, snap back to the real first slide
        const onEnd = () => {
          track.removeEventListener('transitionend', onEnd);
          index = 0;
          apply(false);
        };
        track.addEventListener('transitionend', onEnd);
      }
    };
    const prev = () => {
      if (index <= 0) {
        // jump to a clone position past the end, then animate back one step
        index = total;
        apply(false);
        // Force reflow so the next transform actually animates
        // eslint-disable-next-line no-unused-expressions
        track.offsetWidth;
        index -= 1;
        apply(true);
      } else {
        index -= 1;
        apply(true);
      }
    };
    const start = () => { stop(); if (!userPaused) timer = setInterval(next, delay); };
    const stop  = () => { if (timer) { clearInterval(timer); timer = null; } };

    // Swipe
    let startX = 0, dx = 0, dragging = false;
    track.addEventListener('touchstart', (e) => { dragging = true; startX = e.touches[0].clientX; dx = 0; stop(); }, { passive: true });
    track.addEventListener('touchmove',  (e) => { if (!dragging) return; dx = e.touches[0].clientX - startX; }, { passive: true });
    track.addEventListener('touchend',   () => {
      if (!dragging) return; dragging = false;
      if (Math.abs(dx) > 40) { dx < 0 ? next() : prev(); }
      start();
    });

    // Pause on hover (desktop edge cases)
    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', () => { if (!userPaused) start(); });

    const mq = window.matchMedia('(max-width: 767px)');
    mq.addEventListener?.('change', () => {
      perView = mq.matches ? 1 : 2;
      index = 0;
      ensureClones();
      apply(false);
    });

    ensureClones();
    apply(false);
    start();
  });
});

// predictive-search js
document.addEventListener("DOMContentLoaded", () => {

    initPredictiveSearch(
        "predictive-search-input",
        "predictive-search-results"
    );

    initPredictiveSearch(
        "predictive-search-input-mobile",
        "predictive-search-results-mobile"
    );

});

function initPredictiveSearch(inputId, resultsId){

    const input = document.getElementById(inputId);
    const results = document.getElementById(resultsId);

    if(!input || !results) return;

    let timer;

    input.addEventListener("input", function(){

        clearTimeout(timer);

        const query = this.value.trim();

        if(query.length < 2){
            results.innerHTML = "";
            results.style.display = "none";
            return;
        }

        timer = setTimeout(()=>{

            fetch(`/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=10&section_id=predictive-search`)
            .then(res=>res.text())
            .then(html=>{

                const div=document.createElement("div");
                div.innerHTML=html;

                const section=div.querySelector("#shopify-section-predictive-search");

                if(section){
                    results.innerHTML=section.innerHTML;
                    results.style.display="block";
                }else{
                    results.innerHTML="";
                    results.style.display="none";
                }

            });

        },250);

    });

    document.addEventListener("click",(e)=>{

        if(!e.target.closest(".header-search-wrapper")){

            results.style.display="none";

        }

    });

}

// Zonnic Age Verification Popup
document.addEventListener("DOMContentLoaded", function () {
    const popup = document.getElementById("zonic-age-popup");
    if (!popup) return;
    const yesBtn = document.getElementById("zonic-yes");
    const noBtn = document.getElementById("zonic-no");
    const remember = document.getElementById("rememberAge");
    function lockPage() {
        document.body.style.overflow = "hidden";
    }
    function unlockPage() {
        document.body.style.overflow = "";
    }
    const savedTime = localStorage.getItem("zonicAgeVerified");
    if (savedTime) {
        if (Date.now() < parseInt(savedTime, 10)) {
            popup.classList.remove("is-open");
            unlockPage();
            return;
        } else {
            localStorage.removeItem("zonicAgeVerified");
        }
    }
    // FIX (2026-09-14): the Escape blocker below was registered on `document` and never
    // removed, so after a shopper verified their age, Escape stayed dead for the rest
    // of the page's life — breaking the mega-menu and filter-drawer escape handlers on
    // every Zonnic product page. It is now a named handler that is torn down on close.
    // The popup also uses a class instead of inline display, paired with
    // `.zonic-age-popup{display:none}` in hh-fixes.css, so that a JS failure degrades
    // to "no age gate" rather than a permanently locked, unscrollable page.
    popup.classList.add("is-open");
    lockPage();

    // A11Y FIX (2026-09-15): Escape is suppressed on purpose — an age gate must not be
    // dismissible — but that previously left keyboard users with an opaque overlay they
    // could Tab straight past into the hidden page behind it. Focus now starts on the
    // "YES" button and cycles inside the dialog, so the gate is actually a gate. See the
    // matching role="dialog"/aria-modal markup in sections/main-product.liquid.
    const dialog = popup.querySelector(".zonic-popup-content") || popup;
    const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const blockEscape = function (e) {
        if (e.key === "Escape") { e.preventDefault(); return; }
        if (e.key !== "Tab") return;
        const items = Array.prototype.filter.call(
            dialog.querySelectorAll(FOCUSABLE),
            function (el) { return !el.disabled && el.offsetParent !== null; }
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!dialog.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", blockEscape);
    // requestAnimationFrame so the element is painted (and focusable) before we call focus.
    requestAnimationFrame(function () { yesBtn?.focus(); });

    const dismiss = function () {
        popup.classList.remove("is-open");
        document.removeEventListener("keydown", blockEscape);
        unlockPage();
    };

    yesBtn?.addEventListener("click", function () {
       if (remember && remember.checked) {
            const expiryTime = Date.now() + (30 * 60 * 1000);
            localStorage.setItem("zonicAgeVerified", expiryTime);
        }
        dismiss();
    });
    noBtn?.addEventListener("click", function () {
        window.location.href = "/";
    });
});



document.addEventListener('DOMContentLoaded', function () {

  function initMegaMenu() {

    if (window.innerWidth < 1024 || window.innerWidth > 1440) {
      return;
    }

    document.querySelectorAll('.header__nav-item.has-mega').forEach(function (item) {

      const arrow = item.querySelector('.header__nav-arrow');
      const menu = item.querySelector('.mega-menu');
      // aria-expanded belongs on the control, not on the aria-hidden chevron span.
      // See the note on the hover handler above (2026-09-15).
      const trigger = item.querySelector('.header__nav-link');

      if (!arrow || !menu) return;

      let closeTimer;

      function openMenu() {
        clearTimeout(closeTimer);

        item.classList.add('arrow-menu-open');

        menu.style.display = 'block';
        menu.style.visibility = 'visible';
        menu.style.opacity = '1';
        menu.style.pointerEvents = 'auto';

        trigger?.setAttribute('aria-expanded', 'true');
        menu.setAttribute('aria-hidden', 'false');
      }

      function closeMenu() {
        closeTimer = setTimeout(function () {

          item.classList.remove('arrow-menu-open');

          menu.style.display = '';
          menu.style.visibility = '';
          menu.style.opacity = '';
          menu.style.pointerEvents = '';

          trigger?.setAttribute('aria-expanded', 'false');
          menu.setAttribute('aria-hidden', 'true');

        }, 150);
      }

      /* Hover arrow */
      arrow.addEventListener('mouseenter', openMenu);

      arrow.addEventListener('mouseleave', function () {
        closeMenu();
      });

      /* Keep menu open */
      menu.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        openMenu();
      });

      /* Close after leaving menu */
      menu.addEventListener('mouseleave', function () {
        closeMenu();
      });

    });
  }

  initMegaMenu();

});